import { Pressable, ScrollView, View } from "react-native"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import BFText from "@/components/standard/BFText"
import {
  type CapitalProductRequirement,
  type CapitalProductSlug,
  formatNonLicensedStates,
  formatRequirementValue,
  formatUserActualValue,
  getCapitalProductNameBySlug,
  getNonQualifiedRequirements,
} from "@/screens/loans/utils/LoanCapitalProducts"
import React from "react"
import type {
  CombinedLoanAndProductsQuery,
  ProductType,
} from "@/generated/graphql"

export interface LoanProductCardRequirementsProps {
  cardData: ProductType
  loanData: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
  isSelected: boolean
  onHide: () => void
}

export const LoanProductCardRequirements = ({
  cardData,
  loanData,
  onHide,
}: LoanProductCardRequirementsProps) => {
  const nonQualifiedRequirements: CapitalProductRequirement[] =
    getNonQualifiedRequirements(cardData, loanData)
  const productSlug = cardData.product as CapitalProductSlug
  const title: string = getCapitalProductNameBySlug(productSlug) || "N/A"

  return (
    <View
      className={`flex-1 w-full p-5 bg-grey border rounded border-light-grey overflow-hidden relative`}
    >
      <Pressable
        testID={"loan-card-requirements-hide"}
        onPress={onHide}
        className={"flex-1 w-full"}
      >
        <View className={"pr-5 px-1 justify-end"}>
          <BackButton />
        </View>

        <BFText className="font-figtree-extrabold text-midnight-blue text-[20px] my-1 leading-7">
          {title}
        </BFText>

        <BFText className="font-spacegrotesk text-midnight-blue text-[13px] mt-0 mb-4 leading-none tracking-tight">
          Speak to a lending expert to discuss further
        </BFText>

        <View className="flex flex-row w-full justify-between mt-0">
          <View className="flex-1 mr-2 min-w-[82px]">
            <BFText className="font-spacegrotesk text-[11px] text-dark-grey">
              REQUIREMENTS
            </BFText>
          </View>
          <View className="flex-1 mx-2 min-w-[70px]">
            <BFText className="font-spacegrotesk text-[11px] text-dark-grey">
              TO QUALIFY
            </BFText>
          </View>
          <View className="flex-1 ml-2 min-w-[10px]">
            <BFText className="font-spacegrotesk text-[11px] text-dark-grey">
              YOU
            </BFText>
          </View>
        </View>

        <View className="w-full flex h-px bg-black opacity-40 mt-1 mb-2" />

        <ScrollView className="w-full">
          {nonQualifiedRequirements.map((req) => (
            <View
              key={req.label}
              className="w-full flex-1 flex-row justify-between mt-1"
            >
              <View className="flex-1 min-w-[84px] mr-2">
                <BFText className="font-spacegrotesk text-midnight-blue text-[12px]">
                  {req.label}
                </BFText>
              </View>

              <View className="flex-1 min-w-[70px] mx-2">
                <BFText className="font-figtree-extrabold text-midnight-blue text-[12px]">
                  {formatRequirementValue(
                    req.qualifiedValue,
                    req.unit,
                    req.qualifier || "",
                    req.isPrefix || false
                  )}
                </BFText>
              </View>

              <View className="flex-1 min-w-[10px] ml-2">
                <BFText className="font-figtree-extrabold text-red-orange text-[12px]">
                  {formatUserActualValue(
                    req.userValue,
                    req.unit,
                    req.isPrefix || false
                  )}
                </BFText>
              </View>
            </View>
          ))}
        </ScrollView>

        <BFText
          className="font-spacegrotesk text-dark-grey text-[10px] m-5"
          style={{ position: "absolute", bottom: 0 }}
        >
          **Must not be in{" "}
          {formatNonLicensedStates(cardData.nonLicensedStates as string[])} nor
          some rural locations
        </BFText>
      </Pressable>
    </View>
  )
}
